import {addBridges, computePopoverPos, getEl, padClampSides, runBoth, unionRects} from '../../helpers/tour'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import useSpotlight from '../../hooks/useSpotlight'
import MaskSvg from './SvgMask'
import StepTourContent from './StepTourContent'
import classnames from 'classnames'

const Tour = ({
    steps,
    current,
    initialStep = 0,
    open = true,
    onChange,
    onClose,
    zIndex = 1000,
    maskClosable = false,
    className,
    rectResolver,
    defaultPopoverOffset,
    defaultMergeThreshold = 16,
    beforeNext,
    onNext,
    onFinish,
}) => {
    const uncontrolled = typeof current !== 'number'
    const [idx, setIdx] = useState(initialStep)
    const step = useMemo(() => steps[uncontrolled ? idx : (current ?? 0)] || null, [steps, idx, current, uncontrolled])

    const singleRectResolver = useCallback(
        (s, el) => {
            if (typeof rectResolver === 'function') {
                const res = rectResolver(s, el)
                if (Array.isArray(res) && res.length) return unionRects(res)
                if (res) return res
            }
            if (!el) return null
            return padClampSides(el.getBoundingClientRect(), s?.offset ?? 8)
        },
        [rectResolver],
    )
    const rect = useSpotlight({step, rectResolver: singleRectResolver})

    const holes = step
        ? (() => {
              const el = getEl(step.target)
              const pad = step.offset ?? 0
              const res = typeof rectResolver === 'function' ? rectResolver(step, el) : null
              if (Array.isArray(res) && res.length) return res
              if (res) return [res]
              return el ? [padClampSides(el.getBoundingClientRect(), pad, step.elRadius)] : []
          })()
        : []

    const _t = step?.bridgeThreshold
    const threshold = typeof _t === 'number' && _t > 0 ? _t : defaultMergeThreshold
    const holesWithBridge = addBridges(holes, threshold)

    const anchor = holesWithBridge.length ? unionRects(holesWithBridge) : rect || null

    const placement = step?.placement || 'bottom'
    const __offset = (step && step.popoverOffset) || defaultPopoverOffset || {x: 0, y: 0}
    const pos = step?.getPopoverPos
        ? step.getPopoverPos({
              rect: anchor,
              placement,
              gap: 12,
              viewport: {width: window.innerWidth, height: window.innerHeight},
          }) || computePopoverPos(anchor, placement, 12, __offset)
        : computePopoverPos(anchor, placement, 12, __offset)

    const anchorCx = anchor ? anchor.left + anchor.width / 2 : null
    const anchorCy = anchor ? anchor.top + anchor.height / 2 : null
    const arrowCenterX = anchorCx != null ? anchorCx - pos.left : null
    const arrowCenterY = anchorCy != null ? anchorCy - pos.top : null
    const root = typeof document !== 'undefined' ? document.body : null
    const isOpen = open && !!step

    const setCurrent = useCallback(
        (next) => {
            if (uncontrolled) setIdx(next)
            onChange && onChange(next)
        },
        [uncontrolled, onChange],
    )

    const getIndex = () => (uncontrolled ? idx : (current ?? 0))

    const goNext = async () => {
        const i = getIndex()
        const isLast = i >= steps.length - 1
        const ctx = {
            index: i,
            nextIndex: Math.min(i + 1, steps.length - 1),
            isLast,
            step: steps[i],
            steps,
        }
        const ok = await runBoth(steps[i]?.beforeNext, beforeNext, ctx)
        if (ok === false) return
        setCurrent(ctx.nextIndex)
        const nextStep = steps[ctx.nextIndex]
        await runBoth(nextStep?.onNext, onNext, {...ctx, step: nextStep, index: ctx.nextIndex})
    }
    const goPrev = () => setCurrent(Math.max(getIndex() - 1, 0))
    const finish = () => {
        onClose && onClose()
        onFinish && onFinish()
        setCurrent(0)
    }

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return
        const onKey = (e) => {
            if (e.key === 'Escape') finish()
            if (e.key === 'ArrowRight') goNext()
            if (e.key === 'ArrowLeft') goPrev()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [isOpen, idx, current])

    // Optionally disable pointer events on the target
    useEffect(() => {
        const el = step ? getEl(step.target) : null
        if (el && step?.disableInteraction) {
            const prev = el.style.pointerEvents
            el.style.pointerEvents = 'none'
            return () => {
                el.style.pointerEvents = prev
            }
        }
    }, [step])

    if (!isOpen || !root) return null

    const content = (
        <div aria-live="polite" aria-atomic style={{position: 'fixed', inset: 0, zIndex}}>
            <MaskSvg rects={holesWithBridge} zIndex={zIndex} onClick={maskClosable ? finish : undefined} />

            <div
                role="dialog"
                aria-modal
                style={{
                    position: 'fixed',
                    top: pos.top,
                    left: pos.left,
                    transform: pos.transform,
                    zIndex: zIndex + 1,
                    maxWidth: 400,
                }}
                className={className}
            >
                <StepTourContent
                    index={getIndex()}
                    {...step}
                    totalStep={steps.length}
                    onClose={onClose}
                    onNext={goNext}
                    onFinish={finish}
                    className={classnames('StepTourContent', step.className)}
                    arrowProps={
                        step?.popoverOffset
                            ? {centerX: arrowCenterX, centerY: arrowCenterY, ...(step?.arrowProps || {})}
                            : undefined
                    }
                />
            </div>
        </div>
    )

    return createPortal(content, root)
}

export function btn(variant) {
    const base = {
        appearance: 'none',
        border: '1px solid transparent',
        padding: '8px 12px',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 14,
    }
    if (variant === 'primary') return {...base, background: '#1677ff', color: '#fff'}
    if (variant === 'secondary') return {...base, background: '#f3f4f6', color: '#303238', borderColor: '#D8DBDE'}
    return {...base, background: 'transparent', color: '#666'}
}

export default Tour
